import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import API from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ArrowRight, FileSpreadsheet, Loader2, Eye, ChevronLeft, ChevronRight, Search, X } from "lucide-react";

const PREVIEW_ROW_LIMIT = 12;

export default function Phase7EvidencePreview() {
  const navigate = useNavigate();
  const { auditRun, advancePhase } = useAudit();
  const [loading, setLoading] = useState(false);
  const [artifacts, setArtifacts] = useState([]);
  const [searchQueries, setSearchQueries] = useState({});

  const csvArtifacts = useMemo(
    () => artifacts.filter((artifact) => artifact?.is_csv),
    [artifacts]
  );

  const fetchPreviews = useCallback(async () => {
    if (!auditRun?.id) return;
    try {
      setLoading(true);
      const { data } = await API.get(`/audit-runs/${auditRun.id}/artifacts/previews`, {
        params: { row_limit: PREVIEW_ROW_LIMIT },
      });
      setArtifacts(Array.isArray(data?.artifacts) ? data.artifacts : []);
    } catch (error) {
      console.error("Failed to load artifact previews:", error);
      toast.error("Failed to load artifact previews");
    } finally {
      setLoading(false);
    }
  }, [auditRun?.id]);

  const handleArtifactPageChange = useCallback(async (artifactId, nextPage) => {
    if (!auditRun?.id || !artifactId || nextPage < 1) return;
    try {
      const current = artifacts.find((a) => a.id === artifactId);
      const q = (current?.query || "").trim();
      const { data } = await API.get(`/audit-runs/${auditRun.id}/artifacts/previews`, {
        params: { row_limit: PREVIEW_ROW_LIMIT, artifact_id: artifactId, page: nextPage, q: q || undefined },
      });
      const updated = Array.isArray(data?.artifacts) ? data.artifacts[0] : null;
      if (!updated) return;
      setArtifacts((prev) => prev.map((artifact) => (
        artifact.id === artifactId ? { ...artifact, ...updated } : artifact
      )));
    } catch (error) {
      console.error("Failed to load artifact page:", error);
      toast.error("Failed to load requested page");
    }
  }, [auditRun?.id, artifacts]);

  const handleSearch = useCallback(async (artifactId) => {
    if (!auditRun?.id || !artifactId) return;
    const q = String(searchQueries[artifactId] || "").trim();
    if (!q) {
      toast.info("Enter a search term");
      return;
    }
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/artifacts/previews`, {
        params: { row_limit: PREVIEW_ROW_LIMIT, artifact_id: artifactId, page: 1, q },
      });
      const updated = Array.isArray(data?.artifacts) ? data.artifacts[0] : null;
      if (!updated) return;
      setArtifacts((prev) => prev.map((artifact) => (
        artifact.id === artifactId ? { ...artifact, ...updated } : artifact
      )));
    } catch (error) {
      console.error("Search failed:", error);
      toast.error("Search failed");
    }
  }, [auditRun?.id, searchQueries]);

  const clearSearch = useCallback(async (artifactId) => {
    if (!auditRun?.id || !artifactId) return;
    setSearchQueries((prev) => ({ ...prev, [artifactId]: "" }));
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/artifacts/previews`, {
        params: { row_limit: PREVIEW_ROW_LIMIT, artifact_id: artifactId, page: 1 },
      });
      const updated = Array.isArray(data?.artifacts) ? data.artifacts[0] : null;
      if (!updated) return;
      setArtifacts((prev) => prev.map((artifact) => (
        artifact.id === artifactId ? { ...artifact, ...updated } : artifact
      )));
    } catch (error) {
      console.error("Failed to clear search:", error);
      toast.error("Failed to reset preview");
    }
  }, [auditRun?.id]);

  useEffect(() => {
    fetchPreviews();
  }, [fetchPreviews]);

  const handleContinue = async () => {
    await advancePhase(8);
    navigate("/phase/8");
  };

  return (
    <div className="space-y-6" data-testid="phase-7-preview">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Evidence Preview</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Review uploaded artifact CSV rows before drafting findings.
          </p>
        </div>
        <Button variant="outline" onClick={fetchPreviews} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
          Refresh Preview
        </Button>
      </div>

      {loading && (
        <Card>
          <CardContent className="py-12 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Loading artifact previews...
          </CardContent>
        </Card>
      )}

      {!loading && csvArtifacts.length === 0 && (
        <Card>
          <CardContent className="min-h-[240px] flex flex-col items-center justify-center text-center px-6">
            <FileSpreadsheet className="w-9 h-9 text-blue-500/70 mb-3" />
            <p className="text-sm font-medium">No CSV artifacts available for preview.</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-md">
              Upload CSV artifacts in Phase 5, then run Evidence Evaluation to cross-check rows here before Findings.
            </p>
          </CardContent>
        </Card>
      )}

      {!loading && csvArtifacts.map((artifact) => (
        <Card key={artifact.id}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4 text-blue-500" />
              <span>{artifact.filename}</span>
              <Badge variant="outline" className="ml-1 text-xs">
                {artifact.row_count ?? 0} rows
              </Badge>
              {artifact.query ? (
                <Badge variant="outline" className="ml-1 text-xs">
                  {artifact.match_count ?? 0} match{(artifact.match_count ?? 0) === 1 ? "" : "es"}
                </Badge>
              ) : null}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {artifact.warning ? (
              <div className="rounded-md border border-blue-300/60 bg-blue-50/60 p-3 text-xs text-blue-700">
                {artifact.warning}
              </div>
            ) : null}
            {artifact.error ? (
              <div className="rounded-md border border-amber-300/60 bg-amber-50/60 p-3 text-xs text-amber-700">
                {artifact.error}
              </div>
            ) : artifact.headers?.length > 0 && artifact.rows?.length > 0 ? (
              <div className="space-y-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="flex-1">
                    <Input
                      value={searchQueries[artifact.id] ?? artifact.query ?? ""}
                      onChange={(e) => setSearchQueries((prev) => ({ ...prev, [artifact.id]: e.target.value }))}
                      placeholder="Search this CSV (e.g., SKU000010, store_id, status)..."
                      className="h-9"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleSearch(artifact.id)}>
                      <Search className="w-3.5 h-3.5 mr-1" />
                      Search
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => clearSearch(artifact.id)} disabled={!artifact.query}>
                      <X className="w-3.5 h-3.5 mr-1" />
                      Clear
                    </Button>
                  </div>
                </div>
                <div className="rounded-md border border-border overflow-x-auto">
                  <Table className="table-dense">
                    <TableHeader>
                      <TableRow>
                        {artifact.headers.map((header) => (
                          <TableHead key={`${artifact.id}-${header}`} className="whitespace-nowrap">
                            {header}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {artifact.rows.map((row, idx) => (
                        <TableRow key={`${artifact.id}-row-${idx}`}>
                          {artifact.headers.map((header) => (
                            <TableCell key={`${artifact.id}-${idx}-${header}`} className="font-mono text-xs">
                              {row[header] ?? ""}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {(artifact.total_pages || 1) > 1 && (
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      Page {artifact.page || 1} of {artifact.total_pages}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleArtifactPageChange(artifact.id, (artifact.page || 1) - 1)}
                        disabled={(artifact.page || 1) <= 1}
                      >
                        <ChevronLeft className="w-3.5 h-3.5 mr-1" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleArtifactPageChange(artifact.id, (artifact.page || 1) + 1)}
                        disabled={(artifact.page || 1) >= (artifact.total_pages || 1)}
                      >
                        Next
                        <ChevronRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                No preview rows were parsed from this CSV.
              </p>
            )}
          </CardContent>
        </Card>
      ))}

      <div className="flex justify-end">
        <Button onClick={handleContinue} data-testid="continue-to-findings-draft-btn">
          <ArrowRight className="w-4 h-4 mr-2" /> Continue to Findings Draft
        </Button>
      </div>
    </div>
  );
}
